import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { lightTheme, darkTheme } from './index';
import { buttonThemes as lightButtonThemes, iconThemes as lightIconThemes, modalThemes as lightModalThemes } from '../globalConstants/LightThemeVariables';
import { buttonThemes as darkButtonThemes, iconThemes as darkIconThemes, modalThemes as darkModalThemes } from '../globalConstants/DarkThemeVariables';
import type { ThemeMode } from './figmaOrganizedTokens';

/** Shape of the theme context value */
export interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  buttonThemes: Record<string, any>;
  iconThemes: Record<string, any>;
  modalThemes: Record<string, any>;
  muiTheme: Theme;
}

/**
 * Context for managing theme state and providing theme-aware variables.
 */
export const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  toggleTheme: () => {},
  buttonThemes: lightButtonThemes,
  iconThemes: lightIconThemes,
  modalThemes: lightModalThemes,
  muiTheme: lightTheme,
});

/**
 * Custom hook to consume the theme context.
 * Must be used inside a ThemeProvider.
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * ThemeProvider wraps the application and provides theme context to all descendants.
 * Persists the selected mode in localStorage and syncs CSS variables accordingly.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get initial theme from localStorage or default to light
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedTheme = localStorage.getItem('theme-mode');
    return (savedTheme === 'dark' ? 'dark' : 'light') as ThemeMode;
  });

  // Update CSS variables based on current theme using Figma tokens
  useEffect(() => {
    const root = document.documentElement;

    if (mode === 'dark') {
      // Set dark theme CSS variables from Figma tokens
      root.style.setProperty('--app-red', '#d22730'); // Figma primary red
      root.style.setProperty('--app-medium-gray', '#878787'); // Figma neutral
      root.style.setProperty('--app-light-gray', '#222222'); // Figma dark sidebar
      root.style.setProperty('--app-dark-gray', '#e0e0e0'); // Figma light gray
      root.style.setProperty('--app-custom-gray', '#878787'); // Figma secondary text

      // Dark theme specific colors
      root.style.setProperty('--background-default', '#222222'); // Figma sidebar dark
      root.style.setProperty('--background-paper', '#353535'); // Figma header dark
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#e0e0e0'); // Figma inverse muted
    } else {
      // Set light theme CSS variables from Figma tokens
      root.style.setProperty('--app-red', '#d22730'); // Figma primary red
      root.style.setProperty('--app-medium-gray', '#878787'); // Figma secondary text
      root.style.setProperty('--app-light-gray', '#e0e0e0'); // Figma borders
      root.style.setProperty('--app-dark-gray', '#111111'); // Figma primary text
      root.style.setProperty('--app-custom-gray', '#878787'); // Figma neutral

      // Light theme specific colors
      root.style.setProperty('--background-default', '#ffffff');
      root.style.setProperty('--background-paper', '#f5f5f5'); // Figma background alt
      root.style.setProperty('--text-primary', '#111111'); // Figma primary text
      root.style.setProperty('--text-secondary', '#878787'); // Figma secondary text
    }
  }, [mode]);

  const toggleTheme = (): void => {
    const newMode: ThemeMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  const currentMuiTheme = mode === 'light' ? lightTheme : darkTheme;
  const currentButtonThemes = mode === 'light' ? lightButtonThemes : darkButtonThemes;
  const currentIconThemes = mode === 'light' ? lightIconThemes : darkIconThemes;
  const currentModalThemes = mode === 'light' ? lightModalThemes : darkModalThemes;

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme,
    buttonThemes: currentButtonThemes,
    iconThemes: currentIconThemes,
    modalThemes: currentModalThemes,
    muiTheme: currentMuiTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={currentMuiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
